import React, { useRef, useMemo } from 'react';
import clsx from 'clsx';
import type { StylesConfig, Button } from '@builder4/shared/types/styles';
import type { ButtonSize } from '@builder4/shared/styles/constants';
import {
  getButtonStyleFromData,
  getColor,
  checkIsGradient,
  getGlobalStyleColor,
} from '@builder4/shared/utils/styleHelpers';
import { useGlobalTextColorStyle } from '@/hooks/useGlobalTextColorStyle';
import type { Color } from '@builder4/shared/types/layout';
import { useButtonScale } from '@/hooks/useButtonScale';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface StylePreviewProps {
  className?: string;
  onClick?: () => void;
  styles: StylesConfig;
  // Optional Tailwind class names to control font-family via classnames (e.g., font-['DM_Sans'])
  headingFontClassName?: string;
  bodyFontClassName?: string;
  // Optional typography token classes (size/weight) for the left labels
  headingTypographyClassName?: string; // default: 'text-button-lg font-medium'
  bodyTypographyClassName?: string; // default: 'text-button-lg font-normal'
  // Optional typography token classes for the right captions
  headingCaptionClassName?: string; // default: 'text-button-sm font-medium'
  bodyCaptionClassName?: string; // default: 'text-button-sm font-medium'
  headingLabel?: string; // label on the right of heading font, default "Headings"
  bodyLabel?: string; // label on the right of body font, default "Body"
  buttonSize?: ButtonSize;
}

// A compact visual preview of the project's typography, colors, and buttons
export const StylePreview: React.FC<StylePreviewProps> = ({
  className = '',
  onClick,
  styles,
  headingFontClassName,
  bodyFontClassName,
  headingTypographyClassName,
  bodyTypographyClassName,
  headingCaptionClassName,
  bodyCaptionClassName,
  headingLabel = 'Headings',
  bodyLabel = 'Body',
  buttonSize = 'medium',
}) => {
  const handleClick = () => {
    if (onClick) onClick();
  };

  const headingFamily = styles.typography.heading.fontFamily;
  const bodyFamily = styles.typography.body.fontFamily;
  const styleColors = styles.colors;

  // Helper to generate a key from a color value for React re-renders
  const getColorKey = (color: Color | string | null | undefined): string => {
    return color ? JSON.stringify(color) : '';
  };

  // Helper to get background color CSS with fallback
  const getBackgroundColorCssSafe = (
    backgroundColor: Color | string | null,
  ): string | undefined => {
    return backgroundColor ? getGlobalStyleColor(backgroundColor, styleColors) : undefined;
  };

  // Helper to create parent div override style to prevent text color from applying to parent
  const createParentDivOverrideStyle = (
    textColorStyle: React.CSSProperties,
    backgroundColorCss?: string,
  ): React.CSSProperties => {
    const override: React.CSSProperties = {
      color: 'transparent',
    };

    if (backgroundColorCss) {
      override.background = backgroundColorCss;
    } else if (textColorStyle.background) {
      // If there's a gradient but no background, set to transparent to prevent gradient from showing
      override.background = 'transparent';
    }

    if (textColorStyle.background) {
      override.WebkitTextFillColor = 'initial';
    }

    return override;
  };

  const headingTextColorStyle = useGlobalTextColorStyle(
    styles.typography.heading.h1.fontColor,
    styles.colors,
  );
  const headingBgColorCss = getBackgroundColorCssSafe(styles.typography.heading.h1.backgroundColor);

  const headingStyle: React.CSSProperties = {
    fontFamily: styles.typography.heading.fontFamily,
    fontWeight: styles.typography.heading.h1.fontWeight,
    letterSpacing: `${styles.typography.heading.h1.letterSpacing}px`,
    ...headingTextColorStyle,
  };

  const headingBgStyle = createParentDivOverrideStyle(headingTextColorStyle, headingBgColorCss);

  const bodyMedium = styles.typography.body.medium;
  const bodyTextColorStyle = useGlobalTextColorStyle(bodyMedium.fontColor, styles.colors);
  const bodyBgColorCss = getBackgroundColorCssSafe(bodyMedium.backgroundColor);

  const bodyStyle: React.CSSProperties = {
    fontFamily: styles.typography.body.fontFamily,
    fontWeight: bodyMedium.fontWeight,
    letterSpacing: `${bodyMedium.letterSpacing}px`,
    ...bodyTextColorStyle,
  };

  const bodyBgStyle = createParentDivOverrideStyle(bodyTextColorStyle, bodyBgColorCss);

  const colorSwatches = ['color6', 'color5', 'color4', 'color3', 'color2', 'color1'].map((k) => {
    const color = styles.colors[k];
    return {
      colorString: getColor(color.value),
      isGradient: checkIsGradient(color.value),
    };
  });

  const button1 = styles.buttons.button1;
  const button2 = styles.buttons.button2;

  const getButtonStyle = (button: Button): string => {
    return getButtonStyleFromData(
      button,
      buttonSize,
      styles.colors,
      styles.buttons.fontSizeUnit || 'px',
    );
  };

  const button1ContainerRef = useRef<HTMLDivElement | null>(null);
  const button1Ref = useRef<HTMLDivElement | null>(null);
  const button2ContainerRef = useRef<HTMLDivElement | null>(null);
  const button2Ref = useRef<HTMLDivElement | null>(null);
  const button1Scale = useButtonScale(button1ContainerRef, button1Ref, 0, [
    buttonSize,
    (button1 as Button)[buttonSize].padding,
    (button1 as Button).borderWidth,
  ]);
  const button2Scale = useButtonScale(button2ContainerRef, button2Ref, 0, [
    buttonSize,
    (button2 as Button)[buttonSize].padding,
    (button2 as Button).borderWidth,
  ]);

  const sharedScale = useMemo(() => {
    const scales = [button1Scale, button2Scale];
    const min = Math.min(...scales);
    const roundedPercent = Math.round(min * 100);
    return roundedPercent >= 100 ? 1 : roundedPercent / 100;
  }, [button1Scale, button2Scale]);

  return (
    <div onClick={handleClick} className={clsx('inline-flex w-[232px] flex-col gap-3', className)}>
      {/* Card */}
      <div className="rounded-lg bg-neutral-50 p-3">
        {/* Typography */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div style={headingBgStyle} className="inline-block">
              <span
                key={getColorKey(styles.typography.heading.h1.fontColor)}
                className={clsx(
                  headingTypographyClassName ?? 'text-button-lg font-medium',
                  (!headingTextColorStyle.color ||
                    (headingTextColorStyle.color === 'transparent' &&
                      !headingTextColorStyle.background)) &&
                    'text-neutral-900',
                  headingFontClassName,
                )}
                style={headingStyle}
              >
                {headingFamily}
              </span>
            </div>
            <span
              className={clsx(
                headingCaptionClassName ?? 'text-button-sm font-medium',
                'text-neutral-500',
              )}
            >
              {headingLabel}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div style={bodyBgStyle} className="inline-block">
              <span
                key={getColorKey(bodyMedium.fontColor)}
                className={clsx(
                  bodyTypographyClassName ?? 'text-button-lg font-normal',
                  (!bodyTextColorStyle.color ||
                    (bodyTextColorStyle.color === 'transparent' &&
                      !bodyTextColorStyle.background)) &&
                    'text-neutral-900',
                  bodyFontClassName,
                )}
                style={bodyStyle}
              >
                {bodyFamily}
              </span>
            </div>
            <span
              className={clsx(
                bodyCaptionClassName ?? 'text-button-sm font-medium',
                'text-neutral-500',
              )}
            >
              {bodyLabel}
            </span>
          </div>
        </div>

        <div className="relative mt-3 flex items-center gap-2">
          {colorSwatches.map((swatch, idx) => (
            <span
              key={idx}
              className={clsx('rounded border border-zinc-300', 'h-4.5', 'w-4.5')}
              style={
                swatch.isGradient
                  ? { backgroundImage: swatch.colorString }
                  : { backgroundColor: swatch.colorString }
              }
            />
          ))}
          {sharedScale < 0.99 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="ml-auto cursor-help rounded bg-neutral-100 px-1.5 py-0.5">
                    <span className="text-[10px] leading-none font-medium text-neutral-900">
                      {Math.round(sharedScale * 100)}%
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-white">
                  <span>Zoom Out {Math.round(sharedScale * 100)}%</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Buttons */}
        <div className="mt-3 flex items-center">
          {/* Primary Button */}
          <div
            ref={button1ContainerRef}
            className="relative flex flex-1 items-center justify-center overflow-hidden"
            style={{ height: 48, padding: 0 }}
          >
            <div
              style={{
                transform: `scale(${sharedScale})`,
                transformOrigin: 'center center',
                willChange: 'transform',
                width: 'fit-content',
                height: 'fit-content',
              }}
            >
              <div
                ref={button1Ref}
                role="presentation"
                aria-hidden="true"
                className={clsx(
                  'inline-flex items-center justify-center select-none',
                  getButtonStyle(button1 as Button),
                )}
                style={{
                  cursor: 'default',
                }}
              >
                <span>Primary</span>
              </div>
            </div>
          </div>

          {/* Secondary Button */}
          <div
            ref={button2ContainerRef}
            className="relative flex flex-1 items-center justify-center overflow-hidden"
            style={{ height: 48, padding: 0 }}
          >
            <div
              style={{
                transform: `scale(${sharedScale})`,
                transformOrigin: 'center center',
                willChange: 'transform',
                width: 'fit-content',
                height: 'fit-content',
              }}
            >
              <div
                ref={button2Ref}
                role="presentation"
                aria-hidden="true"
                className={clsx(
                  'inline-flex items-center justify-center select-none',
                  getButtonStyle(button2 as Button),
                )}
                style={{
                  cursor: 'default',
                }}
              >
                <span>Secondary</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
